<script setup>
import { useI18n } from 'vue-i18n'
import moment from "moment";
import axios from '@/helpers/axios'
import AddWebcamDrawer from "@/views/webcams/AddWebcamDrawer.vue";
import EditWebcamDrawer from "@/views/webcams/EditWebcamDrawer.vue";
import GenerateTimelapseDrawer from '@/views/webcams/GenerateTimelapseDrawer.vue'


definePage({
  meta: {
    layout: 'default',
    role: 'admin'
  },
})

const { t } = useI18n()

const searchQuery = ref('')
const rowPerPage = ref(10)
const currentPage = ref(1)
const totalPage = ref(1)
const totalItems = ref(0)
const items = ref([])
const isAddWebcamDrawerVisible = ref(false)
const isEditWebcamDrawerVisible = ref(false)
const isTimelapseDrawerVisible = ref(false)
const selectedWebcam = ref(null)
const selectedTimelapseWebcam = ref(null)

const getWebcams = () => {
  axios.get('/admin/webcams', {
    params: {
      q: searchQuery.value,
      perPage: rowPerPage.value,
      currentPage: currentPage.value,
    },
  }).then(response => {
    items.value = response.data.data
    totalPage.value = response.data.meta.last_page
    totalItems.value = response.data.meta.total
  }).catch(e => {
    console.error(e)
  })
}

const openEditDrawer = webcam => {
  selectedWebcam.value = webcam
  isEditWebcamDrawerVisible.value = true
}

const openTimelapseDrawer = webcam => {
  selectedTimelapseWebcam.value = webcam
  isTimelapseDrawerVisible.value = true
}

watch(() => isEditWebcamDrawerVisible.value, val => {
  if (!val)
    selectedWebcam.value = null
})

watch(() => isTimelapseDrawerVisible.value, val => {
  if (!val)
    selectedTimelapseWebcam.value = null
})

watchEffect(() => {
  getWebcams()
})


watchEffect(() => {
  if (currentPage.value > totalPage.value)
    currentPage.value = totalPage.value
})


// 👉 Computing pagination data
const paginationData = computed(() => {
  const firstIndex = items.value.length ? (currentPage.value - 1) * rowPerPage.value + 1 : 0
  const lastIndex = items.value.length + (currentPage.value - 1) * rowPerPage.value
  
  return t('tables.showing', { firstIndex, lastIndex, totalItems: totalItems.value })
})
</script>

<template>
  <VCard
    v-if="items"
    id="items-list"
    :title="$t('navigation.webcams')"
  >
    <VCardText class="d-flex align-center flex-wrap gap-4">
      <!-- 👉 Rows per page -->
      <div
        class="d-flex align-center"
        style="width: 150px;"
      >
        <span class="text-no-wrap me-3">{{ $t('tables.show') }}</span>
        <VSelect
          v-model="rowPerPage"
          density="compact"
          :items="[10, 20, 30, 50]"
        />
      </div>

      <VSpacer />

      <div class="d-flex align-center flex-wrap gap-4">
        <!-- 👉 Search  -->
        <div class="items-list-filter">
          <VTextField
            v-model="searchQuery"
            :placeholder="$t('webcams.search_webcam')"
            density="compact"
          />
        </div>
        <VBtn
          prepend-icon="tabler-plus"
          @click="isAddWebcamDrawerVisible = true"
        >
          {{ $t('webcams.add_webcam') }}
        </VBtn>
      </div>
    </VCardText>

    <VDivider />

    <!-- SECTION Table -->
    <VTable class="text-no-wrap items-list-table">
      <!-- 👉 Table head -->
      <thead class="text-uppercase">
        <tr>
          <th scope="col">
            {{ $t('webcams.name') }}
          </th>

          <th scope="col">
            {{ $t('webcams.active') }}
          </th>

          <th scope="col">
            {{ $t('webcams.public') }}
          </th>

          <th scope="col">
            {{ $t('webcams.online') }}
          </th>

          <th scope="col">
            {{ $t('webcams.images') }}
          </th>

          <th scope="col">
            {{ $t('webcams.last_data') }}
          </th>

          <th scope="col" />
        </tr>
      </thead>

      <!-- 👉 Table Body -->
      <tbody>
        <tr
          v-for="item in items"
          :key="item.uuid"
          style="height: 3.75rem;"
        >
          <!-- 👉 Name -->
          <td>
            {{ item.name }}
          </td>

          <td>
            <VChip
              size="small"
              label
              :color="item.active ? 'success' : 'error'"
              variant="tonal"
              class="text-capitalize"
            >
              {{ item.active ? $t('webcams.active') : $t('webcams.inactive') }}
            </VChip>
          </td>

          <td>
            <VChip
              size="small"
              label
              :color="item.public ? 'success' : 'secondary'"
              variant="tonal"
              class="text-capitalize"
            >
              {{ item.public ? $t('webcams.public') : $t('webcams.private') }}
            </VChip>
          </td>

          <td>
            <VChip
              size="small"
              label
              :color="item.online ? 'success' : 'error'"
              variant="tonal"
              class="text-capitalize"
            >
              {{ item.online ? $t('webcams.online') : $t('webcams.offline') }}
            </VChip>
          </td>

          <td>
            {{ item.images }}
          </td>

          <!-- 👉 Date -->
          <td>
            {{ item.last_data ? moment.utc(item.last_data).local().format("Y-MM-DD HH:mm") : '' }}
          </td>

           <!-- 👉 Actions -->
           <td style="width: 10rem;">
              <VBtn
                icon
                variant="text"
                color="default"
                size="x-small"
                class="me-1"
                @click="openEditDrawer(item)"
              >
                <VIcon
                  :size="22"
                  icon="tabler-pencil"
                />
              </VBtn>
              <VBtn
                icon
                variant="text"
                color="default"
                size="x-small"
                class="me-1"
                :title="$t('webcams.generate_timelapse')"
                @click="openTimelapseDrawer(item)"
              >
                <VIcon
                  :size="22"
                  icon="tabler-video"
                />
              </VBtn>
              <VBtn
                icon
                variant="text"
                color="default"
                size="x-small"
                :to="item.last_data
                  ? { name: 'webcams-uuid', params: { uuid: item.uuid }, query: { last_data: moment(item.last_data).format('YYYY-MM-DD') } }
                  : { name: 'webcams-uuid', params: { uuid: item.uuid } }"
              >
                <VIcon
                  :size="22"
                  icon="tabler-eye"
                />
              </VBtn>
            </td>
        </tr>
      </tbody>

      <!-- 👉 table footer  -->
      <tfoot v-show="!items.length">
        <tr>
          <td
            colspan="8"
            class="text-center text-body-1"
          >
            {{ $t('webcams.no_webcams') }}
          </td>
        </tr>
      </tfoot>
    </VTable>
    <!-- !SECTION -->

    <VDivider />

    <!-- SECTION Pagination -->
    <VCardText class="d-flex align-center flex-wrap gap-4 py-3">
      <!-- 👉 Pagination meta -->
      <span class="text-sm text-disabled">
        {{ paginationData }}
      </span>

      <VSpacer />

      <!-- 👉 Pagination -->
      <VPagination
        v-model="currentPage"
        size="small"
        :total-visible="5"
        :length="totalPage"
      />
    </VCardText>
    <!-- !SECTION -->
  </VCard>
  <AddWebcamDrawer
    :is-drawer-open="isAddWebcamDrawerVisible"
    @update:is-drawer-open="(val) => isAddWebcamDrawerVisible = val"
    @submit="getWebcams"
  />
  <EditWebcamDrawer
    :is-drawer-open="isEditWebcamDrawerVisible"
    :webcam="selectedWebcam"
    @update:is-drawer-open="(val) => isEditWebcamDrawerVisible = val"
    @submit="getWebcams"
  />
  <GenerateTimelapseDrawer
    :is-drawer-open="isTimelapseDrawerVisible"
    :webcam="selectedTimelapseWebcam"
    @update:is-drawer-open="val => isTimelapseDrawerVisible = val"
    @submit="getWebcams"
  />
</template>

<style lang="scss">
#items-list {
  .items-list-actions {
    inline-size: 8rem;
  }

  .items-list-filter {
    inline-size: 12rem;
  }
}
</style>
